import { useState, useEffect } from "react";
import { MdMarkEmailRead } from "react-icons/md";
import { getAllContactForm } from "../../utils/api";
import { MdOutlineDoNotDisturbOnTotalSilence } from "react-icons/md";
import { FaBookOpen, FaCalendarWeek } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { FaUserCircle } from "react-icons/fa";
import { IoMdCloseCircle } from "react-icons/io";

const AdminPanel = () => {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAllContactForm();
      const contactsData = response?.contacts || [];
      const formatedContacts = contactsData.map((contact) => ({
        _id: contact._id,
        fullName: contact?.fullName || "",
        email: contact.email || "",
        helpText: contact.message || "",
        createdAt: contact.createdAt,
      }));

      setSubmissions(formatedContacts);
    } catch (error) {
      console.error("Error loading contacts:", error);
      setError(error?.response?.data?.message || "Failed to load contacts");
      setSubmissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredSubmissions = submissions.filter((submission) => {
    const matchesSearch =
      submission.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.helpText.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const handleRefresh = () => {
    fetchContacts();
  };

  return (
    <div className="w-full h-full ">
      <div className="min-h-screen hero_bg text-black p-6 pt-24">
        <div className="w-[90%] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-black mb-2">
                Contact Submissions
              </h1>
              <p className="text-gray-400">
                Manage and review contact form submissions
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                className="w-fit flex gap-2 justify-center items-center border-2 border-blue-600 rounded-md px-3 py-1 text-blue-400 hover:text-black hover:bg-blue-600 transition-colors duration-300"
              >
                <MdMarkEmailRead className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Loading/Error/Content using ternary operators */}
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-gray-400">Loading contacts...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-6">
              <p className="text-red-400">{error}</p>
              <button
                onClick={fetchContacts}
                className="mt-2 text-red-300 hover:text-red-100 underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className=" rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Submissions</p>
                      <p className="text-2xl font-bold text-black">
                        {submissions.length}
                      </p>
                    </div>
                    <MdOutlineDoNotDisturbOnTotalSilence className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-transparent rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">This Week</p>
                      <p className="text-2xl font-bold text-black">
                        {
                          submissions.filter(
                            (s) =>
                              new Date(s.createdAt) >
                              new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                          ).length
                        }
                      </p>
                    </div>
                    <FaCalendarWeek className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <CiSearch className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search submissions..."
                    className="w-full bg-transparent border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Content based on data availability */}
              {submissions.length > 0 ? (
                <>
                  {/* Submissions Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredSubmissions.map((submission) => (
                      <div
                        key={submission._id}
                        className="bg-transparent rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-all duration-200 cursor-pointer group"
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                              <FaUserCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-black">
                                {submission.fullName}
                              </h3>
                              <p className="text-sm text-gray-400">
                                {submission.email}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSubmission(submission);
                              }}
                            >
                              <FaBookOpen className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <p className="text-sm text-gray-300 mb-4 line-clamp-3">
                          {submission.helpText}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* No filtered results */}
                  {filteredSubmissions.length === 0 && searchTerm && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-transparent rounded-full flex items-center justify-center mx-auto mb-4">
                        <MdMarkEmailRead className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-400">
                        No submissions found matching your search criteria.
                      </p>
                      <button
                        onClick={() => setSearchTerm("")}
                        className="mt-2 text-blue-400 hover:text-blue-300 underline"
                      >
                        Clear search
                      </button>
                    </div>
                  )}
                </>
              ) : (
                /* No Data Available */
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-transparent rounded-full flex items-center justify-center mx-auto mb-4">
                    <MdMarkEmailRead className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-black mb-2">
                    No Data Available
                  </h3>
                  <p className="text-gray-400 mb-4">
                    There are no contact submissions to display at the moment.
                  </p>
                  <button
                    onClick={fetchContacts}
                    className="bg-blue-600 hover:bg-blue-700 text-black px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    Refresh Data
                  </button>
                </div>
              )}
            </>
          )}

          {/* Detail Modal */}
          {selectedSubmission && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
              <div className="bg-gray-200 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-black">
                      Submission Details
                    </h2>
                    <button
                      className="text-gray-400 hover:text-black"
                      onClick={() => setSelectedSubmission(null)}
                    >
                      <IoMdCloseCircle className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400">Full Name</label>
                      <p className="text-black font-medium">
                        {selectedSubmission.fullName || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400">Email</label>
                    <p className="text-black font-medium">
                      {selectedSubmission.email || "N/A"}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400">Message</label>
                    <p className="text-black p-4 rounded-lg whitespace-pre-wrap">
                      {selectedSubmission.helpText || "No message provided"}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400">Submitted</label>
                    <p className="text-black font-medium">
                      {formatDate(selectedSubmission.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
